import { useEffect, useState } from 'react';
import type { AfterActionResponse, AfterActionEntryKind } from '@tsr/shared-types';
import {
  fetchAfterAction,
  openAfterAction,
  addAfterActionEntry,
  finalizeAfterAction,
  exportAfterActionPdf,
} from './api';
import { getSessionId } from './lib/operatorActions';
import * as v from './afterActionVocabulary';

/**
 * After-Action Report (#612) — the closing beat of the incident workflow, on the Situation-Unit
 * surface (Ontario IMS 2.0 §8 → After-Action Report, App A #1). Mounts inside
 * IncidentManagementSection ONLY when the incident is deactivated (a live incident may not emit a
 * closing document). The FACTUAL SPINE is read-only (rollups verbatim from the Final SITREP; the
 * reactivation is a counted beat; totals are floors when anything is unknown). The AUTHORED half
 * is human-written — the COP records what Planning writes and authors nothing itself; no entry may
 * name an individual (the person-evaluation ban, App A #1).
 */

const ENTRY_KINDS: AfterActionEntryKind[] = ['finding', 'recommendation', 'debrief_note'];

function fmt(iso: string | null): string {
  return iso ? iso.replace('T', ' ').replace('Z', ' UTC') : '—';
}

function hoursBetween(a: string, b: string): string {
  const ms = Date.parse(b) - Date.parse(a);
  return ms > 0 ? (ms / 3_600_000).toFixed(1) : '0.0';
}

export function AfterActionReportPanel({ incidentId }: { incidentId: string }): JSX.Element | null {
  const [data, setData] = useState<AfterActionResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [confirmFinal, setConfirmFinal] = useState(false);

  const [kind, setKind] = useState<AfterActionEntryKind>('finding');
  const [entryBody, setEntryBody] = useState('');
  const [author, setAuthor] = useState('');
  const [section, setSection] = useState('');

  const sessionId = getSessionId();

  useEffect(() => {
    const ctl = new AbortController();
    void fetchAfterAction(incidentId, ctl.signal)
      .then((d) => setData(d))
      .catch((e: unknown) => {
        if (!ctl.signal.aborted) {
          setErr(e instanceof Error ? e.message : 'Failed to load report');
        }
      });
    return () => ctl.abort();
  }, [incidentId, reload]);

  const refresh = (): void => setReload((n) => n + 1);

  async function run(fn: () => Promise<AfterActionResponse | void>): Promise<void> {
    setBusy(true);
    setErr(null);
    try {
      const d = await fn();
      if (d) setData(d);
      else refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  }

  function submitEntry(): void {
    if (!entryBody.trim() || !author.trim() || busy) return;
    void run(async () => {
      const d = await addAfterActionEntry(incidentId, {
        kind,
        body: entryBody.trim(),
        authored_by: author.trim(),
        section: section.trim() || null,
        session_id: sessionId,
      });
      setEntryBody('');
      setSection('');
      return d;
    });
  }

  function exportPdf(): void {
    setBusy(true);
    setErr(null);
    void exportAfterActionPdf(incidentId, { session_id: sessionId })
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank', 'noopener');
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      })
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : 'Export failed'))
      .finally(() => setBusy(false));
  }

  // Invisible until real data resolves (and on a null/aborted resolve) — a mount-fetch that
  // hasn't resolved must not pollute the surface. A genuine load error shows a compact note.
  if (!data) {
    if (err) {
      return (
        <div className="mt-3 text-[11px] text-slate-400" data-testid="after-action-report">
          Could not load the {v.AFTER_ACTION_HEADING}.
        </div>
      );
    }
    return null;
  }

  const report = data.report;
  const isFinal = report?.status === 'final';
  const rollups = data.factual.rollups;
  const floor = rollups.totals_are_floors ? '≥ ' : '';

  return (
    <div
      className="mt-3 rounded border border-slate-600/60 bg-slate-800/40 p-2.5"
      data-testid="after-action-report"
    >
      <div className="flex items-center justify-between">
        <h4 className="text-[12px] font-semibold text-slate-100">{v.AFTER_ACTION_HEADING}</h4>
        {report && (
          <span
            className={`text-[10px] px-1.5 py-0.5 rounded ${
              isFinal ? 'bg-emerald-700/40 text-emerald-200' : 'bg-slate-600/50 text-slate-200'
            }`}
            data-testid="aar-status-badge"
          >
            {isFinal ? v.FINAL_BADGE : v.DRAFT_BADGE}
          </span>
        )}
      </div>
      <p className="mt-1 text-[10.5px] text-slate-400">{v.AFTER_ACTION_CAPTION}</p>

      {/* The person-evaluation ban — a persistent one-line notice on the surface (App A #1). */}
      <p
        className="mt-2 text-[10px] leading-snug text-amber-200/90 border-l-2 border-amber-500/60 pl-2"
        data-testid="aar-person-eval-notice"
      >
        {data.person_evaluation_notice}
      </p>

      {err && <p className="mt-2 text-[11px] text-rose-300">{err}</p>}

      {!data.incident_deactivated && (
        <p className="mt-2 text-[11px] text-slate-400">{v.NOT_DEACTIVATED_NOTE}</p>
      )}

      {data.incident_deactivated && !report && (
        <button
          type="button"
          data-testid="aar-open"
          disabled={busy}
          onClick={() => void run(() => openAfterAction(incidentId, sessionId))}
          className="mt-2 text-[11px] px-2 py-1 rounded border border-sky-500/60 text-sky-200 hover:bg-sky-600/20 disabled:opacity-50"
        >
          {v.OPEN_REPORT_LABEL}
        </button>
      )}

      {report && (
        <div className="mt-2 space-y-2 text-[11px] text-slate-200">
          {/* ── Reactivation — a COUNTED beat, never smoothed ── */}
          <section>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">
              {v.REACTIVATION_HEADING}
            </div>
            {data.factual.reactivation.cycles === 0 ? (
              <div className="text-slate-300">{v.NO_REACTIVATION_NOTE}</div>
            ) : (
              <div data-testid="aar-reactivation">
                <div className="text-amber-200">
                  {v.reactivationCyclesLabel(data.factual.reactivation.cycles)}
                </div>
                <ul className="mt-0.5 list-disc pl-4 text-slate-300">
                  {data.factual.reactivation.gaps.map((g, i) => (
                    <li key={i}>
                      Deactivation #{i + 1}: {fmt(g.deactivated_at)} → {hoursBetween(g.deactivated_at, g.reactivated_at)} h
                      gap → Reactivated {fmt(g.reactivated_at)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </section>

          {/* ── Operational rollups — verbatim from the Final SITREP ── */}
          <section>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">{v.ROLLUPS_HEADING}</div>
            {!rollups.final_sitrep_id ? (
              <div className="text-slate-400">{v.NO_FINAL_SITREP_NOTE}</div>
            ) : (
              <div data-testid="aar-rollups">
                <div className="text-[10px] text-slate-400">{v.ROLLUPS_SOURCE_NOTE}</div>
                {rollups.community_impact && (
                  <ul className="mt-0.5 list-disc pl-4 text-slate-300">
                    <li>Communities: {rollups.community_impact.community_count}</li>
                    <li>
                      Evacuated: {floor}
                      {rollups.community_impact.total_evacuated}
                    </li>
                    <li>
                      Accounted for: {floor}
                      {rollups.community_impact.total_accounted_for}
                    </li>
                    <li>Remaining: {rollups.community_impact.total_remaining}</li>
                    <li className="text-amber-200/90">
                      Unreachable (unknown): {rollups.community_impact.total_unreachable}
                    </li>
                    {rollups.communities_pending > 0 && (
                      <li className="text-amber-200/90">
                        Communities with no recorded headcount: {rollups.communities_pending}
                      </li>
                    )}
                  </ul>
                )}
                {rollups.totals_are_floors ? (
                  <div className="mt-0.5 text-amber-200/90" data-testid="aar-floors-note">
                    {v.FLOORS_NOTE}
                  </div>
                ) : rollups.all_accounted_for ? (
                  <div className="mt-0.5 text-slate-400">{v.ALL_ACCOUNTED_NOTE}</div>
                ) : null}
                {rollups.resource_requests && (
                  <div className="mt-0.5 text-slate-300">
                    Resources — open {rollups.resource_requests.total_open} · committed{' '}
                    {rollups.resource_requests.committed} · demobilized{' '}
                    {rollups.resource_requests.demobilized}
                  </div>
                )}
              </div>
            )}
          </section>

          {/* ── The record spine at a glance ── */}
          <section className="text-slate-300">
            {data.factual.operational_periods.length} operational period
            {data.factual.operational_periods.length === 1 ? '' : 's'} ·{' '}
            {data.factual.sitrep_chain.length} anchored Situation Report
            {data.factual.sitrep_chain.length === 1 ? '' : 's'} · {data.factual.recorded_action_count} recorded
            actions
          </section>

          {/* ── Authored review — findings / recommendations / debrief notes ── */}
          <section>
            <div className="text-[10px] uppercase tracking-wide text-slate-400">
              {v.AFTER_ACTION_REVIEW_HEADING}
            </div>
            {data.entries.length === 0 ? (
              <div className="text-slate-400">{v.EMPTY_ENTRIES_NOTE}</div>
            ) : (
              ENTRY_KINDS.map((k) => {
                const rows = data.entries.filter((e) => e.kind === k);
                if (rows.length === 0) return null;
                return (
                  <div key={k} className="mt-1">
                    <div className="text-[10.5px] font-semibold text-slate-200">
                      {v.ENTRY_KIND_HEADING[k]}
                    </div>
                    <ul className="list-disc pl-4 text-slate-300" data-testid={`aar-entries-${k}`}>
                      {rows.map((e) => (
                        <li key={e.id}>
                          {e.section ? <span className="text-slate-400">[{e.section}] </span> : null}
                          {e.body}
                          <span className="text-[9.5px] text-slate-500"> — {e.authored_by}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })
            )}
          </section>

          {/* ── Add an authored entry (draft only) ── */}
          {!isFinal && (
            <section className="rounded border border-slate-600/50 p-2" data-testid="aar-add-form">
              <div className="flex flex-wrap gap-1.5">
                <select
                  value={kind}
                  onChange={(e) => setKind(e.target.value as AfterActionEntryKind)}
                  className="text-[11px] bg-slate-900 border border-slate-600 rounded px-1 py-0.5 text-slate-200"
                  data-testid="aar-kind"
                >
                  {ENTRY_KINDS.map((k) => (
                    <option key={k} value={k}>
                      {v.ENTRY_KIND_LABEL[k]}
                    </option>
                  ))}
                </select>
                <input
                  value={section}
                  onChange={(e) => setSection(e.target.value)}
                  placeholder={v.ENTRY_SECTION_PLACEHOLDER}
                  className="flex-1 min-w-[80px] text-[11px] bg-slate-900 border border-slate-600 rounded px-1 py-0.5 text-slate-200"
                />
              </div>
              <textarea
                value={entryBody}
                onChange={(e) => setEntryBody(e.target.value)}
                placeholder={v.ENTRY_BODY_PLACEHOLDER}
                rows={2}
                className="mt-1 w-full text-[11px] bg-slate-900 border border-slate-600 rounded px-1 py-0.5 text-slate-200"
                data-testid="aar-body"
              />
              <div className="mt-1 flex gap-1.5">
                <input
                  value={author}
                  onChange={(e) => setAuthor(e.target.value)}
                  placeholder={v.ENTRY_AUTHOR_PLACEHOLDER}
                  className="flex-1 text-[11px] bg-slate-900 border border-slate-600 rounded px-1 py-0.5 text-slate-200"
                  data-testid="aar-author"
                />
                <button
                  type="button"
                  disabled={busy || !entryBody.trim() || !author.trim()}
                  onClick={submitEntry}
                  className="text-[11px] px-2 py-0.5 rounded border border-sky-500/60 text-sky-200 hover:bg-sky-600/20 disabled:opacity-40"
                  data-testid="aar-add-entry"
                >
                  {v.ADD_ENTRY_LABEL}
                </button>
              </div>
            </section>
          )}

          {/* ── Finalize + export ── */}
          <div className="flex flex-wrap gap-1.5 pt-1">
            {!isFinal &&
              (confirmFinal ? (
                <span className="flex items-center gap-1.5">
                  <span className="text-[10px] text-slate-300">Finalize (immutable)?</span>
                  <button
                    type="button"
                    disabled={busy || !author.trim()}
                    onClick={() =>
                      void run(() => finalizeAfterAction(incidentId, author.trim() || 'Planning', sessionId)).then(
                        () => setConfirmFinal(false),
                      )
                    }
                    className="text-[11px] px-2 py-0.5 rounded border border-emerald-500/60 text-emerald-200 hover:bg-emerald-600/20 disabled:opacity-40"
                    data-testid="aar-finalize-confirm"
                    title="Finalizing locks the report — a final report is immutable"
                  >
                    Confirm
                  </button>
                  <button
                    type="button"
                    onClick={() => setConfirmFinal(false)}
                    className="text-[11px] px-2 py-0.5 rounded border border-slate-500/60 text-slate-300 hover:bg-slate-600/20"
                  >
                    Cancel
                  </button>
                </span>
              ) : (
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => setConfirmFinal(true)}
                  className="text-[11px] px-2 py-0.5 rounded border border-emerald-500/60 text-emerald-200 hover:bg-emerald-600/20 disabled:opacity-40"
                  data-testid="aar-finalize"
                >
                  {v.FINALIZE_LABEL}
                </button>
              ))}
            {data.can_export && (
              <button
                type="button"
                disabled={busy}
                onClick={exportPdf}
                className="text-[11px] px-2 py-0.5 rounded border border-slate-400/60 text-slate-200 hover:bg-slate-600/20 disabled:opacity-40"
                data-testid="aar-export"
              >
                {v.EXPORT_PDF_LABEL}
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
